import type { DataProvider } from '@refinedev/core';
import { apiClient } from '../services/api/apiClient';
import { API_BASE_URL } from '../utils/constants';

/**
 * Refine Data Provider
 * Implements the Refine DataProvider interface for API communication
 * Uses our configured apiClient with JWT authentication
 */
export const dataProvider = (apiUrl: string = API_BASE_URL): DataProvider => ({
  /**
   * Get list of resources
   * @param resource - Resource name (e.g., 'users', 'posts')
   * @param params - Query parameters (pagination, filters, sorting)
   */
  getList: async ({ resource, pagination, filters, sorters }) => {
    // No agregamos apiUrl porque apiClient ya tiene baseURL configurado
    const url = `/${resource}`;

    // Handle pagination - Refine uses 'current' and 'pageSize'
    const query: Record<string, any> = {};
    
    if (pagination) {
      // Refine v4 uses 'current' but some versions use 'currentPage'
      const currentPage = (pagination as any).current ?? (pagination as any).currentPage ?? 1;
      const pageSize = (pagination as any).pageSize ?? 10;
      
      // Django REST Framework PageNumberPagination (standardized across all endpoints)
      query.page = currentPage;
      query.page_size = pageSize;
    }

    // Add filters to query
    if (filters) {
      filters.forEach((filter) => {
        if ('field' in filter) {
          // Handle search parameter specially
          if (filter.field === 'q') {
            query.search = filter.value;
          } else {
            query[filter.field] = filter.value;
          }
        }
      });
    }

    // Add sorting to query
    if (sorters && sorters.length > 0) {
      const ordering = sorters
        .map((sorter) => {
          const prefix = sorter.order === 'desc' ? '-' : '';
          return `${prefix}${sorter.field}`;
        })
        .join(',');
      query.ordering = ordering;
    }

    try {
      const { data } = await apiClient.get(url, { params: query });

      // Handle paginated response — new Refine format { data, total }
      if (data && typeof data === 'object' && 'data' in data && 'total' in data) {
        return {
          data: Array.isArray(data.data) ? data.data : [],
          total: data.total || 0,
        };
      }

      // Handle paginated response — legacy DRF format { results, count }
      if (data && typeof data === 'object' && 'results' in data) {
        return {
          data: Array.isArray(data.results) ? data.results : [],
          total: data.count || 0,
        };
      }

      // Handle direct array response
      if (Array.isArray(data)) {
        return {
          data: data,
          total: data.length,
        };
      }

      // Fallback: return empty array if data structure is unexpected
      console.warn('Unexpected data structure from API:', data);
      return {
        data: [],
        total: 0,
      };
    } catch (error: any) {
      throw error;
    }
  },

  /**
   * Get single resource by ID
   * @param resource - Resource name
   * @param params - Resource ID and metadata
   */
  getOne: async ({ resource, id }) => {
    const url = `/${resource}/${id}/`;

    try {
      const { data } = await apiClient.get(url);

      return {
        data,
      };
    } catch (error: any) {
      throw error;
    }
  },

  /**
   * Create new resource
   * @param resource - Resource name
   * @param params - Resource data
   */
  create: async ({ resource, variables }) => {
    const url = `/${resource}/`;

    try {
      const { data } = await apiClient.post(url, variables);

      return {
        data,
      };
    } catch (error: any) {
      throw error;
    }
  },

  /**
   * Update existing resource
   * @param resource - Resource name
   * @param params - Resource ID and updated data
   */
  update: async ({ resource, id, variables, meta }) => {
    // Handle custom actions (e.g., upload_audit_act)
    let url = `/${resource}/${id}/`;
    let method = 'patch';
    
    if (meta?.customAction) {
      url = `/${resource}/${id}/${meta.customAction}/`;
      method = 'post'; // Custom actions typically use POST
    }

    try {
      const { data } = method === 'post' 
        ? await apiClient.post(url, variables)
        : await apiClient.patch(url, variables);

      return {
        data,
      };
    } catch (error: any) {
      throw error;
    }
  },

  /**
   * Delete resource
   * @param resource - Resource name
   * @param params - Resource ID
   */
  deleteOne: async ({ resource, id }) => {
    const url = `/${resource}/${id}/`;

    try {
      const { data } = await apiClient.delete(url);

      return {
        data,
      };
    } catch (error: any) {
      throw error;
    }
  },

  /**
   * Get API URL
   */
  getApiUrl: () => apiUrl,

  /**
   * Custom method for handling custom API endpoints
   */
  custom: async ({ url, method, payload, query, headers }) => {
    // Refine prepends apiUrl (e.g. '/api') to the url before calling this method.
    // Since apiClient already has baseURL='/api', we need to strip the prefix to avoid duplication.
    let requestUrl = url.startsWith(apiUrl) ? url.slice(apiUrl.length) : url;

    // Add query parameters
    if (query) {
      const queryString = new URLSearchParams(query).toString();
      requestUrl = `${requestUrl}?${queryString}`;
    }

    try {
      const { data } = await apiClient.request({
        url: requestUrl,
        method: method || 'get',
        data: payload,
        headers,
      });

      return {
        data,
      };
    } catch (error: any) {
      throw error;
    }
  },
});

export default dataProvider;
